import startInDomain, { MAX_LIST_START } from "@ext/markdown/elements/list/edit/models/orderList/logic/startInDomain";
import type { Node } from "@tiptap/pm/model";
import { type EditorState, Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import { Mapping } from "@tiptap/pm/transform";

const listContinuationKey = new PluginKey("listContinuation");

const ORDERED_LIST = "orderedList";
const LIST_TYPES = new Set(["orderedList", "bulletList", "taskList"]);

type Wrapper = { node: Node; pos: number; end: number; depth: number; parent: Node };

type OldList = {
	node: Node;
	parent: Node;
	depth: number;
	start: number;
	count: number;
	/** Опоры: позиция внутри каждого пункта и позиция внутри самого списка — на случай, если пункты заменены все разом. */
	anchors: number[];
	predecessor?: OldList;
	home?: Wrapper;
	/** Части разорванного списка после дома: пояснение посреди списка делит его на несколько оболочек. */
	fragments: Wrapper[];
	fragmentTargets: (number | undefined)[];
	/** Список того же родителя, чей новый узел принял пункты этого списка (слияние `JoinLists`). */
	absorbedBy?: OldList;
	moved: boolean;
	target?: number;
};

const insideTextblock = (doc: Node, from: number, to: number) => {
	const size = doc.content.size;
	const From = doc.resolve(Math.min(from, size));
	return From.node(From.sharedDepth(Math.min(to, size))).isTextblock;
};

/** Правка целиком внутри абзацев списки не задевает: ни в старом документе, ни в новом. */
const touchesBlocks = (transactions: readonly Transaction[]) =>
	transactions.some((tr) =>
		tr.steps.some((step, i) => {
			const before = tr.docs[i];
			const after = tr.docs[i + 1] ?? tr.doc;
			let touches = false;
			step.getMap().forEach((oldStart, oldEnd, newStart, newEnd) => {
				if (!insideTextblock(before, oldStart, oldEnd) || !insideTextblock(after, newStart, newEnd))
					touches = true;
			});
			return touches;
		}),
	);

/** Обход блоков с глубиной и родителем — `descendants` глубину не дает, а `resolve` на каждый узел линеен по детям корня. */
const walkBlocks = (doc: Node, visit: (node: Node, pos: number, parent: Node, depth: number) => void) => {
	const walk = (parent: Node, pos: number, depth: number) =>
		parent.forEach((node, offset) => {
			visit(node, pos + offset, parent, depth);
			if (!node.isTextblock && !node.isLeaf) walk(node, pos + offset + 1, depth + 1);
		});
	walk(doc, 0, 1);
};

const collectOldLists = (doc: Node): OldList[] => {
	const lists: OldList[] = [];
	const lastByParent = new Map<Node, OldList>();
	walkBlocks(doc, (node, pos, parent, depth) => {
		if (node.type.name !== ORDERED_LIST) return;
		// Опоры в порядке документа: сама оболочка, потом пункты — по ним части разорванного списка идут слева направо.
		const anchors: number[] = [pos + 1];
		node.forEach((_, offset) => anchors.push(pos + 1 + offset + 1));
		const list: OldList = {
			node,
			parent,
			depth,
			start: startInDomain(node.attrs.start),
			count: node.childCount,
			anchors,
			fragments: [],
			fragmentTargets: [],
			moved: false,
		};
		const predecessor = lastByParent.get(parent);
		if (predecessor && list.start === predecessor.start + predecessor.count) list.predecessor = predecessor;
		lastByParent.set(parent, list);
		lists.push(list);
	});
	return lists;
};

const collectWrappers = (doc: Node): Wrapper[] => {
	const wrappers: Wrapper[] = [];
	walkBlocks(doc, (node, pos, parent, depth) => {
		if (LIST_TYPES.has(node.type.name)) wrappers.push({ node, pos, end: pos + node.nodeSize, depth, parent });
	});
	return wrappers;
};

/** Ближайшая оболочка списка вокруг каждой позиции — одним проходом по оболочкам и по позициям в порядке документа. */
const wrappersAround = (wrappers: Wrapper[], positions: number[]) => {
	const around = new Map<number, Wrapper>();
	const open: Wrapper[] = [];
	let next = 0;
	for (const pos of [...new Set(positions)].sort((a, b) => a - b)) {
		for (; next < wrappers.length && wrappers[next].pos < pos; next++) {
			while (open.length && open[open.length - 1].end <= wrappers[next].pos) open.pop();
			open.push(wrappers[next]);
		}
		while (open.length && open[open.length - 1].end <= pos) open.pop();
		if (open.length) around.set(pos, open[open.length - 1]);
	}
	return around;
};

/** Дом — нумерованная оболочка на прежней глубине вокруг живой опоры, если ее не занял список раньше по документу. */
const findHomes = (lists: OldList[], mapping: Mapping, newDoc: Node) => {
	const mapped = lists.map((list) => list.anchors.map((anchor) => mapping.mapResult(anchor, 1)));
	const alivePositions = mapped
		.flat()
		.filter((result) => !result.deletedAcross)
		.map((result) => result.pos);
	const wrappers = collectWrappers(newDoc);
	const around = wrappersAround(wrappers, alivePositions);
	const claimed = new Map<number, OldList>();
	const homeless: OldList[] = [];
	lists.forEach((list, i) => {
		let alive = false;
		let owner: OldList | undefined;
		for (const result of mapped[i]) {
			if (result.deletedAcross) continue;
			const wrapper = around.get(result.pos);
			// Опора вне списка или на другой глубине — пункт поднят, опущен или ушел во внешний список.
			if (!wrapper || wrapper.depth !== list.depth) continue;
			alive = true;
			// Оболочка чужого типа сбор частей не обрывает: пункт мог стать маркированным, а части списка — остаться ниже.
			if (wrapper.node.type.name !== ORDERED_LIST) continue;
			const claimant = claimed.get(wrapper.pos);
			if (claimant) {
				if (claimant !== list) owner ??= claimant;
				continue;
			}
			// Первая нумерованная оболочка — дом, следующие у того же родителя — части разорванного списка.
			if (!list.home) list.home = wrapper;
			else if (wrapper.parent === list.home.parent) list.fragments.push(wrapper);
			else continue;
			claimed.set(wrapper.pos, list);
		}
		if (!alive) homeless.push(list);
		else if (!list.home && owner?.parent === list.parent) list.absorbedBy = owner;
	});
	const spare = wrappers.filter(({ node, pos }) => node.type.name === ORDERED_LIST && !claimed.has(pos));
	for (const list of homeless) {
		const index = spare.findIndex(({ node }) => node.eq(list.node));
		if (index === -1) continue;
		list.moved = true;
		spare.splice(index, 1);
	}
};

const partNumber = (wrapper: Wrapper, target?: number) => target ?? startInDomain(wrapper.node.attrs.start);

/** Последний номер списка считается по его последней части: разрыв продолжает счет, а не начинает заново. */
const lastNumber = (list: OldList) => {
	const last = list.fragments.at(-1);
	if (last) return partNumber(last, list.fragmentTargets.at(-1)) + last.node.childCount - 1;
	if (!list.home) return undefined;
	return partNumber(list.home, list.target) + list.home.node.childCount - 1;
};

/**
 * Цель списка — номер за последним пунктом предшественника; поглощенный предшественник считается по поглотившему,
 * удаленный отдает собственную цель.
 */
const assignTargets = (lists: OldList[]) => {
	for (const list of lists) {
		const home = list.home;
		// Список, которому та же правка сменила номер, цели не получает: его продолжения считаются от нового номера.
		if (!home || startInDomain(home.node.attrs.start) === list.start) {
			list.target = list.start;
			const previous = list.predecessor;
			if (previous && !previous.moved) {
				const last = lastNumber(previous.absorbedBy ?? previous);
				list.target = last === undefined ? previous.target : last + 1;
			}
		}
		let number = home ? partNumber(home, list.target) : undefined;
		let count = home?.node.childCount ?? 0;
		list.fragments.forEach((fragment, i) => {
			// Часть, чей номер сменила та же правка, цели не получает: следующие считаются от ее номера.
			const kept = startInDomain(fragment.node.attrs.start) === list.start;
			const target = kept && number !== undefined ? number + count : undefined;
			list.fragmentTargets[i] = target;
			number = partNumber(fragment, target);
			count = fragment.node.childCount;
		});
	}
};

const continueLists = (transactions: readonly Transaction[], oldState: EditorState, newState: EditorState) => {
	const mapping = new Mapping();
	for (const tr of transactions) mapping.appendMapping(tr.mapping);

	const lists = collectOldLists(oldState.doc);
	findHomes(lists, mapping, newState.doc);
	assignTargets(lists);

	const tr = newState.tr;
	const renumber = (list: OldList, wrapper: Wrapper | undefined, target: number | undefined) => {
		if (!wrapper || list.moved || target === undefined) return;
		const current = startInDomain(wrapper.node.attrs.start);
		if (current !== list.start || target === current || target < 0 || target > MAX_LIST_START) return;
		tr.setNodeMarkup(wrapper.pos, undefined, { ...wrapper.node.attrs, start: target });
	};
	for (const list of lists) {
		renumber(list, list.home, list.target);
		list.fragments.forEach((fragment, i) => renumber(list, fragment, list.fragmentTargets[i]));
	}
	return tr.steps.length ? tr.setMeta(listContinuationKey, true) : null;
};

/**
 * Считает цепочку по документу до правки, поэтому должен стоять после `JoinLists`: иначе набранный между
 * списками пункт виден отдельным списком и рвет цепочку. Порядок задает `getExtensions` — `JoinLists` там позже
 * `CustomOrderList`, а tiptap собирает плагины в обратном порядке.
 */
const listContinuationPlugin = () =>
	new Plugin({
		key: listContinuationKey,
		appendTransaction: (transactions, oldState, newState) => {
			if (!transactions.some((tr) => tr.docChanged)) return null;
			if (transactions.some((tr) => tr.getMeta(listContinuationKey) || tr.getMeta("history$"))) return null;
			if (!touchesBlocks(transactions)) return null;
			return continueLists(transactions, oldState, newState);
		},
	});

export default listContinuationPlugin;
